import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Navigate, Link, useParams } from 'react-router-dom';
import {
  ApiError,
  dens as densApi,
  events as eventsApi,
  messaging as msgApi,
  type CalendarEvent,
  type DenRow,
  type MessageStatusFilter,
  type MessageSummary,
  type MessageTemplate,
  type RecipientFilter,
  type ResolvedRecipients,
} from '../api';
import { useUnits } from '../units';

const STATUS_LABELS: Record<MessageStatusFilter, string> = {
  dues_unpaid: 'Dues unpaid',
  dues_paid: 'Dues paid',
  fundraiser_unsubmitted: 'Fundraiser not submitted',
  rsvp_pending: 'RSVP pending',
};

export function Messaging() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole, councilCanMessage } = useUnits();
  const canCompose = hasRole('ADMIN') || hasRole('DEN_LEADER') || councilCanMessage;

  const [messages, setMessages] = useState<MessageSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadMessages = useCallback(() => {
    msgApi
      .list(unitId)
      .then(setMessages)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load sent messages'));
  }, [unitId]);

  useEffect(loadMessages, [loadMessages]);

  if (!currentUnit) return <Navigate to="/" replace />;
  if (!canCompose) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>Messaging</h1>
      <p className="muted">{currentUnit.name} · compose &amp; send</p>
      {error && <p className="error">{error}</p>}

      <Compose unitId={unitId} onSent={loadMessages} />

      <h2 style={{ marginTop: 24 }}>Sent</h2>
      {!messages ? (
        <p className="muted">Loading…</p>
      ) : messages.length === 0 ? (
        <p className="muted">Nothing sent yet.</p>
      ) : (
        messages.map((m) => (
          <div className="req-block" key={m.id}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <strong>{m.subject}</strong>
              <span className="muted small">{new Date(m.sentAt).toLocaleString()}</span>
            </div>
            <p className="muted small">
              {m.senderName} · {m.recipientCount} recipient{m.recipientCount === 1 ? '' : 's'}
              <span className="badge" style={{ marginLeft: 6 }}>
                {m.delivery.queued} queued
              </span>
              {m.delivery.sent > 0 && (
                <span className="badge" style={{ marginLeft: 6 }}>
                  {m.delivery.sent} sent
                </span>
              )}
              {m.delivery.bounced > 0 && (
                <span className="badge" style={{ marginLeft: 6 }}>
                  {m.delivery.bounced} bounced
                </span>
              )}
              {m.savedPostId && (
                <Link to={`/units/${unitId}/announcements`} className="badge" style={{ marginLeft: 6 }}>
                  archived
                </Link>
              )}
            </p>
            {m.statusFilter.length > 0 && (
              <p className="muted small">
                Filters: {m.statusFilter.map((s) => STATUS_LABELS[s]).join(', ')}
              </p>
            )}
            <p style={{ whiteSpace: 'pre-wrap' }}>{m.body}</p>
          </div>
        ))
      )}
      <p className="muted small" style={{ marginTop: 12 }}>
        Email delivery is not live yet — messages are recorded and will send once the Resend
        integration is enabled.
      </p>
    </div>
  );
}

function Compose({ unitId, onSent }: { unitId: string; onSent: () => void }) {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [dens, setDens] = useState<DenRow[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [denIds, setDenIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<MessageStatusFilter[]>([]);
  const [eventId, setEventId] = useState('');
  const [saveToArchive, setSaveToArchive] = useState(false);
  const [archiveVisibility, setArchiveVisibility] = useState<'public' | 'members_only'>('members_only');

  const [preview, setPreview] = useState<ResolvedRecipients | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    msgApi.templates(unitId).then(setTemplates).catch(() => setTemplates([]));
    densApi.list(unitId).then((r) => setDens(r.dens)).catch(() => setDens([]));
    eventsApi.list(unitId, 'upcoming').then(setEvents).catch(() => setEvents([]));
  }, [unitId]);

  const filter = useMemo<RecipientFilter>(
    () => ({
      denIds: denIds.length ? denIds : undefined,
      statuses: statuses.length ? statuses : undefined,
      eventId: statuses.includes('rsvp_pending') && eventId ? eventId : undefined,
    }),
    [denIds, statuses, eventId],
  );

  // Any filter change invalidates a stale preview.
  useEffect(() => setPreview(null), [filter]);

  function applyTemplate(key: string) {
    const t = templates.find((x) => x.key === key);
    if (!t) return;
    setSubject(t.subject);
    setBody(t.body);
  }

  function toggleDen(id: string) {
    setDenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }
  function toggleStatus(s: MessageStatusFilter) {
    setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  async function runPreview() {
    setError(null);
    setOkMsg(null);
    setPreviewing(true);
    try {
      setPreview(await msgApi.previewRecipients(unitId, filter));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not resolve recipients');
    } finally {
      setPreviewing(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOkMsg(null);
    setSending(true);
    try {
      const sent = await msgApi.send(unitId, {
        subject,
        body,
        filter,
        saveToArchive,
        archiveVisibility: saveToArchive ? archiveVisibility : undefined,
      });
      setOkMsg(`Recorded for ${sent.recipientCount} recipient${sent.recipientCount === 1 ? '' : 's'}.`);
      setSubject('');
      setBody('');
      setDenIds([]);
      setStatuses([]);
      setEventId('');
      setSaveToArchive(false);
      setPreview(null);
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the message');
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="event-form" onSubmit={submit}>
      <label className="filter">
        Template{' '}
        <select defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
          <option value="">Start from scratch…</option>
          {templates.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <input
        placeholder="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        required
      />
      <textarea
        rows={6}
        placeholder="Write your message…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
      />

      <fieldset className="compose-filters">
        <legend>Recipients</legend>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div>
            <p className="muted small" style={{ margin: '0 0 4px' }}>Dens (all if none checked)</p>
            {dens.map((d) => (
              <label key={d.id} className="filter" style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  checked={denIds.includes(d.id)}
                  onChange={() => toggleDen(d.id)}
                />{' '}
                {d.name}
              </label>
            ))}
          </div>
          <div>
            <p className="muted small" style={{ margin: '0 0 4px' }}>Status (all listed must apply)</p>
            {(Object.keys(STATUS_LABELS) as MessageStatusFilter[]).map((s) => (
              <label key={s} className="filter" style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  checked={statuses.includes(s)}
                  onChange={() => toggleStatus(s)}
                />{' '}
                {STATUS_LABELS[s]}
              </label>
            ))}
            {statuses.includes('rsvp_pending') && (
              <label className="filter" style={{ display: 'block', marginTop: 4 }}>
                Event{' '}
                <select value={eventId} onChange={(e) => setEventId(e.target.value)} required>
                  <option value="">Choose an event…</option>
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.title} · {new Date(ev.startsAt).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 8, alignItems: 'center' }}>
          <button type="button" onClick={runPreview} disabled={previewing}>
            {previewing ? 'Checking…' : 'Preview recipients'}
          </button>
          {preview && (
            <span className="muted small">
              {preview.recipientCount} recipient{preview.recipientCount === 1 ? '' : 's'} ·{' '}
              {preview.scoutCount} Scout{preview.scoutCount === 1 ? '' : 's'}
              {preview.scoutsWithoutContact > 0 &&
                ` · ${preview.scoutsWithoutContact} with no email on file`}
            </span>
          )}
        </div>
        {preview && preview.recipients.length > 0 && (
          <ul className="muted small" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {preview.recipients.slice(0, 25).map((r) => (
              <li key={r.email}>
                {r.userName} &lt;{r.email}&gt; — {r.scouts.map((s) => s.name).join(', ')}
              </li>
            ))}
            {preview.recipients.length > 25 && <li>…and {preview.recipients.length - 25} more</li>}
          </ul>
        )}
      </fieldset>

      <label className="filter">
        <input
          type="checkbox"
          checked={saveToArchive}
          onChange={(e) => setSaveToArchive(e.target.checked)}
        />{' '}
        Also save to the announcements archive
      </label>
      {saveToArchive && (
        <label className="filter">
          Archive visibility{' '}
          <select
            value={archiveVisibility}
            onChange={(e) => setArchiveVisibility(e.target.value as 'public' | 'members_only')}
          >
            <option value="members_only">Members only</option>
            <option value="public">Public</option>
          </select>
        </label>
      )}

      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <button type="submit" className="primary" disabled={sending || !subject || !body}>
          {sending ? 'Sending…' : 'Send message'}
        </button>
        {okMsg && <span className="muted small">{okMsg}</span>}
        {error && <span className="error">{error}</span>}
      </div>
    </form>
  );
}
